import _filter from 'lodash/filter';
import _sortBy from 'lodash/sortBy';
import { LDFlagSet } from 'launchdarkly-js-sdk-common';

import {
  mobile,
  tablet,
  desktop,
  largeDesktop,
  wideScreen,
  ultraWideScreen,
} from './deviceWidths';
import { urlProfile } from '../../server/utils/urlConstants';
import { pwpConfig } from '../components/_common/Cart/pwpConfig';

export const delay = ms => new Promise(r => setTimeout(r, ms));

export const disableRipple = () => {
  return (
    !/Chrome/.test(window.navigator.userAgent) ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(
      window.navigator.userAgent,
    )
  );
};

export function unescapeHTML(html) {
  const escapeEl = document.createElement('div');
  escapeEl.innerHTML = html;
  return escapeEl.textContent;
}

export function reverseRenderImages() {
  [].forEach.call(document.querySelectorAll('img[src]'), img => {
    img.setAttribute('data-src', img.getAttribute('src'));
    img.onload = () => {
      img.removeAttribute('src');
    };
  });
}

export function renderImages() {
  [].forEach.call(document.querySelectorAll('img[data-src]'), img => {
    img.setAttribute('src', img.getAttribute('data-src'));
    img.onload = () => {
      img.removeAttribute('data-src');
    };
  });
}

export function hasClass(el, className) {
  if (el.classList) {
    return el.classList.contains(className);
  }
  return !!el.className.match(new RegExp(`(\\s|^)${className}(\\s|$)`));
}

export function addClass(el, className) {
  if (el) {
    if (el.classList) {
      el.classList.add(className);
    } else if (!hasClass(el, className)) {
      el.className += ` ${className}`;
    }
  }
}

export function removeClass(el, className) {
  if (el) {
    if (el.classList) {
      el.classList.remove(className);
    } else if (hasClass(el, className)) {
      const reg = new RegExp(`(\\s|^)${className}(\\s|$)`);
      el.className = el.className.replace(reg, ' ');
    }
  }
}

export const deviceBuilder = ({ containerWidth }) => {
  switch (true) {
    default:
      return 6;
    case containerWidth <= tablet && containerWidth > mobile:
      return 4;
    case containerWidth <= desktop && containerWidth > largeDesktop:
      return 3;
    case containerWidth <= largeDesktop && containerWidth > tablet:
      return 3;
    case containerWidth <= wideScreen && containerWidth > largeDesktop:
      return 3;
    case containerWidth <= ultraWideScreen && containerWidth > wideScreen:
      return 2;
    case containerWidth > ultraWideScreen:
      return 1.5;
  }
};

export const setISO8601 = string => {
  const regexp =
    '([0-9]{4})(-([0-9]{2})(-([0-9]{2})(T([0-9]{2}):([0-9]{2})(:([0-9]{2})(.([0-9]+))?)?(Z|(([-+])([0-9]{2}):([0-9]{2})))?)?)?)?';
  const d = string.match(new RegExp(regexp));

  let offset = 0;
  const date = new Date(d[1], 0, 1);

  if (d[3]) {
    date.setMonth(d[3] - 1);
  }
  if (d[5]) {
    date.setDate(d[5]);
  }
  if (d[7]) {
    date.setHours(d[7]);
  }
  if (d[8]) {
    date.setMinutes(d[8]);
  }
  if (d[10]) {
    date.setSeconds(d[10]);
  }
  if (d[12]) {
    date.setMilliseconds(Number(`0.${d[12]}`) * 1000);
  }
  if (d[14]) {
    offset = Number(d[16]) * 60 + Number(d[17]);
    offset *= d[15] === '-' ? 1 : -1;
  }

  offset -= date.getTimezoneOffset();
  const time = Number(date) + offset * 60 * 1000;
  const current = new Date();
  current.setTime(Number(time));
  return current;
};

export const formatDate = date => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const day = date.getDate();
  const monthIndex = date.getMonth();
  const year = date.getFullYear();

  return `${monthNames[monthIndex]} ${day}, ${year}`;
};

export const getImageUrl = (image, size) => {
  const imageId = image.match(/(.*)\.[^.]+$/)[1];
  const mimeType = image.match(/[^.]+$/)[0];
  return `${urlProfile}${imageId}_${size}.${mimeType}`;
};

export default function getPageConfig(config) {
  /* filter configs that are in the future and not relevant */
  const configKeys = Object.keys(config);
  const validConfigs = _filter(configKeys, timestamp => {
    const date = new Date(timestamp);
    return date < new Date();
  });
  /* find the first config from those that are valid */
  const sortedConfigs = _sortBy(validConfigs, timestamp => {
    return new Date(timestamp);
  }).reverse();
  return config[sortedConfigs[0]];
}

export function getPwpProduct(flags, productsBulk) {
  if (!flags || !productsBulk) return null;
  const newPwp = pwpConfig[0];
  const oldPwp = pwpConfig[1];

  const newPwpFlag = flags && flags[newPwp.ldFlag];
  const oldPwpFlag = flags && flags[oldPwp.ldFlag];

  let pwpProductId = '';
  if (newPwpFlag) {
    pwpProductId = newPwp.productId;
  } else if (oldPwpFlag) {
    pwpProductId = oldPwp.productId;
  }
  if (pwpProductId === '') return null;

  const productEdge =
    productsBulk &&
    productsBulk.edges &&
    productsBulk.edges.find((edge: any) => {
      if (!edge || !edge.node) return false;
      return edge.node.productId === pwpProductId;
    });
  return productEdge ? productEdge.node : null;
}

export function shouldDisplayPwPDialog({ viaQualifiedCartTotal }) {
  return viaQualifiedCartTotal % 6 === 0 && viaQualifiedCartTotal > 0;
}

export function getPwpConfigItem(productId) {
  const activeItem = pwpConfig.filter(item => productId === item.productId);
  return activeItem.length ? activeItem[0] : null;
}

export function getRewardsFlag(flags?: LDFlagSet) {
  return flags && flags.rewardPoints;
}

export function getOldRewardsCoupon(cart, currentRewardsCode) {
  if (cart && cart.wordPressCoupons && cart.wordPressCoupons.length > 0) {
    const filteredGiftCards =
      cart &&
      cart.wordPressCoupons.filter(
        card =>
          card && !!card.yotpoCouponCode && card.code !== currentRewardsCode,
      );
    if (filteredGiftCards && filteredGiftCards.length > 0) {
      return filteredGiftCards[0];
    }
  }
  return null;
}
